// Beperkt de vragenlijst tot de vragen die de respondent op DIT moment moet
// zien, gegeven zijn antwoorden tot nu toe.
//
// Draait ná filterFieldsForMoment (die de per-meetmoment uitgezette vragen al
// weglaat) en op elk invulpad — publieke vragenlijst en AI-interviewer — zodat
// er geen kanaal is waar een verborgen vraag alsnog langskomt. Eén helper,
// meerdere afnemers.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
//
// DRIE MANIEREN WAAROP EEN VELD VERDWIJNT:
//   1. Zijn eigen `ShowWhen` klopt niet met de gegeven antwoorden.
//   2. Zijn triggervraag is zelf verborgen — anders zou een keten van regels
//      halverwege weer opengaan (C hangt aan B, B is weg, dus C ook).
//   3. Het staat in een sectie die verborgen is.
// Omdat 2 en 3 elkaar kunnen voeden, draait de bepaling tot ze niet meer
// verandert, met een harde bovengrens zodat een kringverwijzing niet vastloopt.
//
// TWEE DINGEN VERDWIJNEN NOOIT:
//   - Meetstandaardvragen (MSI). Een MSI-effect wordt afgemeten aan het
//     verschil tussen voor- en nameting; een respondent die de vraag overslaat
//     levert geen score. Ze zijn immuun voor hun eigen regel én voor een
//     verborgen sectie eromheen. De bouwer hoort een regel op zo'n vraag — en
//     op een sectie die er een bevat — te blokkeren; dit is het vangnet.
//   - Een sectie die van zichzelf al leeg was. Die is een bouwersstaat, geen
//     gevolg van een regel, en mag niet stilletjes verdwijnen.
//
// AI-CONTEXT: See docs/features/conditional-questions.md

import evaluateShowWhen from './evaluateShowWhen'
import isStandardQuestion from './isStandardQuestion'

/**
 * @param {Array<object>} fields - QuestionnaireFields van één vragenlijst,
 *   inclusief de sectiemarkeringen (`Type: 'section'`), in weergavevolgorde.
 * @param {Array<object>} [responses] - de Responses-context van de respondent.
 * @returns {Array<object>} Dezelfde velden in dezelfde volgorde, zonder de
 *   vragen die op dit moment niet gesteld worden en zonder sectiekoppen die
 *   daardoor leeg achterblijven.
 */
const filterFieldsForResponses = (fields, responses) => {
    if (!Array.isArray(fields)) return []

    const answers = Array.isArray(responses) ? responses : []
    const present = new Set(fields.map((field) => field?.ID).filter(Boolean))
    const hidden = new Set()

    // Fixpoint: elke ronde kan een net verborgen veld een volgende meeslepen.
    // Meer rondes dan velden zijn er niet nodig — een kring stopt daardoor ook.
    for (let pass = 0; pass <= fields.length; pass += 1) {
        let changed = false

        for (const field of fields) {
            const id = field?.ID
            if (!id || hidden.has(id)) continue
            if (isStandardQuestion(field)) continue

            const rule = field?.ShowWhen
            const triggerId = rule?.FieldID
            let hide = false

            // Een regel telt alleen als de triggervraag er nog IS. Verwijderd of
            // op dit meetmoment uitgezet → tonen (fail-open): een vraag stil
            // laten verdwijnen is erger dan er één te veel stellen.
            if (triggerId && present.has(triggerId)) {
                hide = hidden.has(triggerId) || !evaluateShowWhen(rule, answers)
            }

            const sectionId = field?.SectionID
            if (!hide && sectionId && hidden.has(sectionId)) hide = true

            if (hide) {
                hidden.add(id)
                changed = true
            }
        }

        if (!changed) break
    }

    if (hidden.size === 0) return fields

    const remaining = fields.filter((field) => !hidden.has(field?.ID))

    const childSections = (list) => new Set(
        list
            .filter((field) => field?.Type !== 'section' && field?.SectionID)
            .map((field) => field.SectionID)
    )
    const hadChildren = childSections(fields)
    const stillHasChildren = childSections(remaining)

    // Een sectiekop zonder vragen eronder is voor de respondent een lege kop.
    // Alleen koppen die vragen HÁDDEN vallen weg — een van zichzelf lege sectie
    // is niet door een regel geraakt en blijft dus staan.
    return remaining.filter((field) => (
        field?.Type !== 'section'
        || !hadChildren.has(field?.ID)
        || stillHasChildren.has(field?.ID)
    ))
}

export default filterFieldsForResponses
